import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutGrid, Shield, ShieldAlert, Users, TrendingUp, Wallet, Banknote,
  Percent, AlertTriangle, FileWarning, ShieldCheck,
  BarChart3, Megaphone, Lock, Calculator, LifeBuoy, LogOut, Receipt, Gift, Smartphone,
  Bell, Search, MessageCircle, Newspaper, Rocket, MessageSquare,
} from "lucide-react";
import { useAdminAuth } from "@/lib/adminAuth";
import BrandLogo from "@/components/common/BrandLogo";

export const ADMIN_NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/admin/blog", label: "Blog", icon: Newspaper, testid: "nav-blog" },
  { to: "/admin/access-control", label: "Access Control", icon: Shield, testid: "nav-access-control" },
  { to: "/admin/users", label: "User Management", icon: Users, testid: "nav-users" },
  { to: "/admin/spot", label: "Spot Trading", icon: TrendingUp, testid: "nav-spot" },
  { to: "/admin/futures", label: "Futures", icon: TrendingUp, testid: "nav-futures" },
  { to: "/admin/wallet", label: "Wallet Management", icon: Wallet, testid: "nav-wallet" },
  { to: "/admin/wallet/approvals", label: "Withdrawal Approvals", icon: Wallet, testid: "nav-wallet-approvals" },
  { to: "/admin/wallet/whitelist-approvals", label: "Withdraw Whitelist", icon: ShieldCheck, testid: "nav-whitelist-approvals" },
  { to: "/admin/wallet/deposit-whitelist-approvals", label: "Deposit Whitelist", icon: ShieldCheck, testid: "nav-deposit-whitelist-approvals" },
  { to: "/admin/inr", label: "INR Payments", icon: Banknote, testid: "nav-inr" },
  { to: "/admin/fees", label: "Fee Management", icon: Percent, testid: "nav-fees" },
  { to: "/admin/finance/tds", label: "TDS Settings", icon: Calculator, testid: "nav-tds-settings" },
  { to: "/admin/finance/tds/transactions", label: "TDS Transactions", icon: Receipt, testid: "nav-tds-txns" },
  { to: "/admin/risk", label: "Risk Management", icon: AlertTriangle, testid: "nav-risk" },
  { to: "/admin/aml", label: "FIU / AML", icon: FileWarning, testid: "nav-aml" },
  { to: "/admin/surveillance", label: "Market Surveillance", icon: ShieldAlert, testid: "nav-surveillance" },
  { to: "/admin/compliance", label: "Risk & Compliance", icon: ShieldCheck, testid: "nav-compliance" },

  { to: "/admin/kyc", label: "KYC Review", icon: ShieldCheck, testid: "nav-kyc" },
  { to: "/admin/kyc/signzy", label: "KYC Mode", icon: ShieldCheck, testid: "nav-signzy-kyc" },
  { to: "/admin/otc", label: "OTC Desk", icon: MessageCircle, testid: "nav-otc" },
  { to: "/admin/reports", label: "Reports & Analytics", icon: BarChart3, testid: "nav-reports" },
  { to: "/admin/cms", label: "CMS & Announcements", icon: Megaphone, testid: "nav-cms" },
  { to: "/admin/launch-gate", label: "Coming Soon Gate", icon: Rocket, testid: "nav-launch-gate" },
  { to: "/admin/marketing/rewards", label: "Rewards & Bonuses", icon: Gift, testid: "nav-rewards" },
  { to: "/admin/marketing/app-releases", label: "App Releases (APK)", icon: Smartphone, testid: "nav-app-releases" },
  { to: "/admin/security", label: "Security Controls", icon: Lock, testid: "nav-security" },
  { to: "/admin/security/sms-dlt", label: "SMS / DLT", icon: MessageSquare, testid: "nav-sms-dlt" },
  { to: "/admin/tax", label: "Taxation & GST", icon: Calculator, testid: "nav-tax" },
  { to: "/admin/support", label: "Support & Disputes", icon: LifeBuoy, testid: "nav-support" },
];

/** Highlight only the best-matching nav item (avoids parent + child both active). */
function isNavItemActive(pathname, itemTo, allItems) {
  if (pathname === itemTo) return true;
  if (!pathname.startsWith(`${itemTo}/`)) return false;
  const childPaths = allItems
    .map((i) => i.to)
    .filter((to) => to !== itemTo && to.startsWith(`${itemTo}/`));
  return !childPaths.some(
    (child) => pathname === child || pathname.startsWith(`${child}/`),
  );
}

export default function AdminShell({ children, title, subtitle }) {
  const { admin, logout } = useAdminAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (admin === false) {
      nav("/admin/login", { replace: true });
      return;
    }
    if (admin) setChecked(true);
  }, [admin, nav]);

  // Wait until admin session is verified — prevents child pages firing API calls without cookies
  if (admin === null || !checked || !admin) {
    return (
      <div className="min-h-screen bg-[#0A0F1F] grid place-items-center text-[#8892A8]">
        Loading admin panel…
      </div>
    );
  }

  const initial = (admin.full_name || admin.email || "?").charAt(0).toUpperCase();
  const adminRole = admin.admin_role || "super_admin";

  return (
    <div className="h-screen overflow-hidden flex bg-[#0A0F1F] text-[#E5E9F2]" data-testid="admin-shell">
      {/* Sidebar */}
      <aside className="w-[260px] shrink-0 h-full bg-[#0F1626] border-r border-[#1E2845] flex flex-col" data-testid="admin-sidebar">
        <Link to="/admin/dashboard" className="flex items-center gap-2.5 px-5 h-[64px] shrink-0 border-b border-[#1E2845]">
          <BrandLogo size="xs" />
          <div className="text-[15px] font-bold">CoinMBX</div>
        </Link>

        <nav className="flex-1 min-h-0 overflow-y-auto py-3 px-2 space-y-0.5">
          {ADMIN_NAV.map((item) => {
            const active = isNavItemActive(loc.pathname, item.to, ADMIN_NAV);
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-[8px] text-[13px] font-medium transition ${
                  active
                    ? "bg-[#F0B83A]/12 text-[#F0B83A]"
                    : "text-[#8892A8] hover:bg-[#13192C] hover:text-[#E5E9F2]"
                }`}
                style={active ? { background: "rgba(240,184,58,0.12)", color: "#F0B83A" } : {}}
              >
                <Icon size={16} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 shrink-0 border-t border-[#1E2845]">
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-[8px] bg-[#13192C]">
            <div className="w-9 h-9 rounded-full bg-[#F0B83A] text-[#0A0F1F] font-bold grid place-items-center text-[14px]">
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-bold truncate">{admin.full_name || admin.email}</div>
              <div className="text-[10px] text-[#8892A8] truncate">{adminRole.replace("_", " ")}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 h-full">
        {/* Top bar */}
        <header className="h-[64px] shrink-0 border-b border-[#1E2845] bg-[#0F1626] flex items-center px-6 gap-4" data-testid="admin-topbar">
          <div className="flex-1 min-w-0">
            <h1 className="text-[18px] font-bold leading-tight">{title}</h1>
            <div className="text-[11px] text-[#8892A8]">{subtitle || "Manage your exchange platform"}</div>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-[#0A0F1F] border border-[#1E2845] rounded-[10px] px-3 h-[40px] w-[280px]">
            <Search size={14} className="text-[#5A6582]" />
            <input
              placeholder="Search..."
              className="bg-transparent outline-none text-[13px] flex-1 text-[#E5E9F2] placeholder:text-[#5A6582]"
              data-testid="admin-search"
            />
          </div>
          <button className="relative w-10 h-10 grid place-items-center text-[#8892A8] hover:text-[#E5E9F2] hover:bg-[#13192C] rounded-[10px]">
            <Bell size={16} />
            <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[#EF4444]" />
          </button>
          <button
            onClick={async () => { await logout(); nav("/admin/login"); }}
            data-testid="admin-logout"
            className="flex items-center gap-1.5 h-[40px] px-4 rounded-[10px] border border-[#1E2845] text-[#E5E9F2] hover:bg-[#13192C] text-[13px] font-semibold"
          >
            <LogOut size={14} /> Logout
          </button>
        </header>

        <main className="flex-1 min-h-0 overflow-hidden p-6 flex flex-col">
          <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden">{children}</div>
        </main>
      </div>
    </div>
  );
}

// ============== Reusable building blocks ==============
export function StatCard({ label, value, sub, icon: Icon, color = "#F0B83A", testid }) {
  return (
    <div
      className="rounded-[14px] p-5 border bg-[#0F1626] relative overflow-hidden"
      style={{ borderColor: "#1E2845" }}
      data-testid={testid}
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ background: `radial-gradient(ellipse at top right, ${color}22 0%, transparent 70%)` }}
      />
      <div className="relative flex items-start justify-between">
        <div>
          <div className="text-[12px] text-[#8892A8] uppercase tracking-wider">{label}</div>
          <div className="text-[28px] font-bold mt-1.5 tabular-nums">{value}</div>
          {sub && <div className="text-[11px] text-[#8892A8] mt-1">{sub}</div>}
        </div>
        {Icon && (
          <div
            className="w-9 h-9 rounded-[10px] grid place-items-center"
            style={{ background: `${color}22`, color }}
          >
            <Icon size={16} />
          </div>
        )}
      </div>
    </div>
  );
}

export function StatusPill({ value, color }) {
  const map = {
    active: ["#10B98122", "#10B981"],
    approved: ["#10B98122", "#10B981"],
    completed: ["#10B98122", "#10B981"],
    healthy: ["#10B98122", "#10B981"],
    cleared: ["#10B98122", "#10B981"],
    resolved: ["#10B98122", "#10B981"],
    pending: ["#F59E0B22", "#F59E0B"],
    "pending review": ["#F59E0B22", "#F59E0B"],
    pending_review: ["#F59E0B22", "#F59E0B"],
    "in_progress": ["#3B82F622", "#3B82F6"],
    "in progress": ["#3B82F622", "#3B82F6"],
    investigating: ["#3B82F622", "#3B82F6"],
    "under review": ["#3B82F622", "#3B82F6"],
    warning: ["#F59E0B22", "#F59E0B"],
    suspended: ["#EF444422", "#EF4444"],
    banned: ["#EF444422", "#EF4444"],
    rejected: ["#EF444422", "#EF4444"],
    failed: ["#EF444422", "#EF4444"],
    critical: ["#EF444422", "#EF4444"],
    halted: ["#EF444422", "#EF4444"],
    high: ["#EF444422", "#EF4444"],
    medium: ["#F59E0B22", "#F59E0B"],
    low: ["#10B98122", "#10B981"],
    escalated: ["#8B5CF622", "#8B5CF6"],
    new: ["#3B82F622", "#3B82F6"],
    contacted: ["#3B82F622", "#3B82F6"],
    qualified: ["#8B5CF622", "#8B5CF6"],
    won: ["#10B98122", "#10B981"],
    lost: ["#EF444422", "#EF4444"],
    open: ["#F59E0B22", "#F59E0B"],
    closed: ["#5A658222", "#8892A8"],
    restricted: ["#F59E0B22", "#F59E0B"],
    disabled: ["#5A658222", "#8892A8"],
    "deposit only": ["#3B82F622", "#3B82F6"],
    deposit_only: ["#3B82F622", "#3B82F6"],
  };
  const key = String(value || "").toLowerCase();
  const [bg, fg] = map[key] || ["#5A658222", "#8892A8"];
  return (
    <span
      className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
      style={{ background: color || bg, color: fg }}
    >
      {value || "—"}
    </span>
  );
}

export function MockBadge({ phase = "B" }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
      style={{ background: "rgba(240,184,58,0.12)", color: "#F0B83A" }}
      data-testid="mock-badge"
    >
      ⚡ Mock data · Phase {phase}
    </span>
  );
}

export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="flex gap-1 border-b border-[#1E2845] mb-5">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          data-testid={`tab-${t.id}`}
          className={`px-4 py-2.5 text-[13px] font-semibold border-b-2 transition ${
            value === t.id
              ? "border-[#F0B83A] text-[#F0B83A]"
              : "border-transparent text-[#8892A8] hover:text-[#E5E9F2]"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Panel({ children, className = "" }) {
  return (
    <div
      className={`rounded-[14px] border border-[#1E2845] bg-[#0F1626] overflow-hidden ${className}`}
    >
      {children}
    </div>
  );
}

export function Btn({ children, kind = "primary", className = "", ...rest }) {
  const styles = {
    primary: { background: "#F0B83A", color: "#0A0F1F" },
    ghost: { background: "transparent", color: "#E5E9F2", border: "1px solid #1E2845" },
    danger: { background: "#EF4444", color: "white" },
    success: { background: "#10B981", color: "white" },
  };
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center gap-1.5 h-[38px] px-4 rounded-[10px] text-[13px] font-bold hover:opacity-90 transition disabled:opacity-50 ${className}`}
      style={styles[kind]}
    >
      {children}
    </button>
  );
}
